import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import type {
	LegacyTimeOfDay,
	TimeOfDayValue,
} from "../../domain/viewer/timeOfDay";
import type { BuildInput } from "../../engines/types";
import { startShaftDustAnimation } from "./shaftDustAnimation";
import {
	isAutomatedViewer,
	resolveAmbientEffectTime,
} from "./ambientEffectClock";
import {
	buildShaftDustGeometry,
	SHAFT_DUST_FRAGMENT_SHADER,
	SHAFT_DUST_VERTEX_SHADER,
} from "./lightShaftDust";
import { buildLightShaftFrustum } from "./lightShaftGeometry";
import {
	getLightShaftIntensity,
	getLightShaftPlacements,
} from "./lightShaftLayout";
import type { ViewerRuntimeProfile } from "./viewerConfig";

/**
 * Evening god rays — fake volumetric light beams spilling outward from
 * glazed openings (issue 076). Deliberately a "fake volumetrics" track:
 * real additive geometry instead of a postprocessing `GodRays` pass,
 * which sidesteps the trap where postprocessing effects silently no-op
 * in our composer (see 2026-06-09 shadow-pipeline note). The beams
 * actually contribute pixels because they ARE geometry.
 *
 * Each beam is a four-sided frustum whose near quad is exactly the
 * opening rectangle — see `lightShaftGeometry.ts` for why (and for what
 * the old apex-at-the-window cone got wrong).
 *
 * Render budget: every beam is STATIC, so on the demand frameloop the
 * shafts cost nothing once painted. Visible evening dust requests at most
 * 6 frames/s, pausing for reduced motion, hidden tabs and automation. The
 * `<shaderMaterial>` is declarative so every beam shares one compiled
 * program (identical source → three reuses the program), and R3F owns
 * uniform updates, repaint invalidation, and disposal. The only
 * imperative pieces are the frustum geometries (they bake the opening's
 * metres), which are disposed explicitly when the set changes.
 *
 * Tier-gated to `balanced`+ via `lightShaftsEnabled`; the adaptive
 * quality monitor can still drop the live tier to `low` under load, in
 * which case the shafts correctly disappear with the rest of the
 * balanced-tier lookdev.
 */

interface LightShaftsProps {
	buildInput: BuildInput;
	runtimeProfile: ViewerRuntimeProfile;
	timeOfDay: TimeOfDayValue | LegacyTimeOfDay | number;
}

/** Full strength at the opening, fading to nothing at the far end. */
const VERTEX_SHADER = /* glsl */ `
varying vec3 vNormalView;
varying vec3 vViewPos;
varying float vLengthFade;
void main() {
	vec4 mv = modelViewMatrix * vec4(position, 1.0);
	vViewPos = mv.xyz;
	vNormalView = normalize(normalMatrix * normal);
	// Geometry runs y = 0 (near quad, on the glass) to y = -1 (far quad).
	// The far end fades to zero alpha, so the flared quad never shows an
	// edge — the beam dissolves into the air instead of stopping.
	vLengthFade = 1.0 + position.y;
	gl_Position = projectionMatrix * mv;
}
`;

/**
 * View-space rim fresnel × length falloff × global intensity. A cheap
 * screen-space hash dither breaks the additive banding that AgX would
 * otherwise quantise into visible rings. Warm amber, tuned to sit on
 * the terracotta/golden-hour palette under additive blending.
 */
const FRAGMENT_SHADER = /* glsl */ `
uniform float uIntensity;
uniform vec3 uColor;
varying vec3 vNormalView;
varying vec3 vViewPos;
varying float vLengthFade;
float hash(vec2 p) {
	return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}
void main() {
	vec3 viewDir = normalize(-vViewPos);
	float fres = 1.0 - abs(dot(viewDir, normalize(vNormalView)));
	fres = pow(clamp(fres, 0.0, 1.0), 1.6);
	float alpha = uIntensity * fres * vLengthFade * 0.1;
	alpha += (hash(gl_FragCoord.xy) - 0.5) * 0.0012;
	// Capped well below 1 so the shaft reads as atmosphere, not a laser —
	// bloom on balanced+ softens it further into a glow.
	alpha = clamp(alpha, 0.0, 0.055);
	gl_FragColor = vec4(uColor, alpha);
}
`;

/** Warm amber. Reads terracotta-adjacent after AgX desaturation. */
const SHAFT_COLOR = new THREE.Color(0xffa860);
/** Soft warm dust without HDR sparkle. */
const DUST_COLOR = new THREE.Color(0xffdfb8);
/** Mote size in CSS px at 1 m, before the per-mote spread. */
const DUST_PIXEL_SIZE_AT_1M = 2.5;

interface ShaftInstance {
	id: string;
	geometry: THREE.BufferGeometry;
	dustGeometry: THREE.BufferGeometry | null;
	position: [number, number, number];
	quaternion: THREE.Quaternion;
	scale: [number, number, number];
}

export function LightShafts({
	buildInput,
	runtimeProfile,
	timeOfDay,
}: LightShaftsProps) {
	const invalidate = useThree((state) => state.invalidate);
	const enabled = runtimeProfile.lightShaftsEnabled;
	const dustEnabled = enabled && runtimeProfile.shaftDustEnabled;
	const intensity = enabled ? getLightShaftIntensity(timeOfDay) : 0;
	// The canvas renders at the device ratio capped by the tier; motes are
	// sized in drawing-buffer pixels, so they must scale by the same ratio.
	const dpr = Math.min(
		typeof window === "undefined" ? 1 : window.devicePixelRatio || 1,
		runtimeProfile.pixelRatioCap,
	);

	const placements = useMemo(
		() => (enabled ? getLightShaftPlacements(buildInput) : []),
		[enabled, buildInput],
	);

	const shafts = useMemo<ShaftInstance[]>(() => {
		return placements.map((placement) => {
			// The frustum bakes the opening's real width/height, so the
			// only thing left to scale is the length axis.
			const { geometry, quaternion, scale } = buildLightShaftFrustum(placement);
			return {
				id: placement.id,
				geometry,
				dustGeometry: dustEnabled ? buildShaftDustGeometry(placement) : null,
				position: placement.origin,
				quaternion,
				scale,
			};
		});
	}, [placements, dustEnabled]);

	// Fresh uniforms object whenever the glow changes — R3F reassigns it
	// and invalidates the demand frameloop for a single repaint. Shared
	// across every beam's material (all glow at the same strength).
	const uniforms = useMemo(
		() => ({
			uIntensity: { value: intensity },
			uColor: { value: SHAFT_COLOR },
		}),
		[intensity],
	);

	// Dispose shaft geometries when the set changes or on unmount. (R3F
	// disposes the declarative materials itself.)
	useEffect(
		() => () => {
			for (const shaft of shafts) {
				shaft.geometry.dispose();
				shaft.dustGeometry?.dispose();
			}
		},
		[shafts],
	);

	const dustUniforms = useMemo(
		() => ({
			uTime: { value: resolveAmbientEffectTime(0) },
			uIntensity: { value: intensity },
			uColor: { value: DUST_COLOR },
			uPixelSize: { value: DUST_PIXEL_SIZE_AT_1M * dpr },
		}),
		[intensity, dpr],
	);
	// Every mote cloud shares one material, so one time write moves them
	// all. A separate, visibility-gated timer requests at most 6 frames/s.
	const dustMaterial = useMemo(
		() =>
			dustEnabled
				? new THREE.ShaderMaterial({
						vertexShader: SHAFT_DUST_VERTEX_SHADER,
						fragmentShader: SHAFT_DUST_FRAGMENT_SHADER,
						uniforms: dustUniforms,
						transparent: true,
						depthWrite: false,
						blending: THREE.AdditiveBlending,
					})
				: null,
		[dustEnabled, dustUniforms],
	);
	const dustMaterialRef = useRef<THREE.ShaderMaterial | null>(null);
	useEffect(() => {
		dustMaterialRef.current = dustMaterial;
		return () => dustMaterial?.dispose();
	}, [dustMaterial]);
	useFrame((state) => {
		const material = dustMaterialRef.current;
		if (!material) return;
		material.uniforms.uTime.value = resolveAmbientEffectTime(
			state.clock.elapsedTime,
		);
	});

	useEffect(() => {
		if (
			!dustEnabled ||
			intensity <= 0 ||
			shafts.length === 0 ||
			isAutomatedViewer()
		)
			return;
		return startShaftDustAnimation(invalidate);
	}, [dustEnabled, intensity, shafts.length, invalidate]);

	if (!enabled || intensity <= 0 || shafts.length === 0) return null;

	return (
		<group name="lightShafts/root">
			{shafts.map((shaft) => (
				<mesh
					key={shaft.id}
					name={`lightShafts/beam/${shaft.id}`}
					geometry={shaft.geometry}
					position={shaft.position}
					quaternion={shaft.quaternion}
					scale={shaft.scale}
					renderOrder={2}
					raycast={() => null}
				>
					<shaderMaterial
						vertexShader={VERTEX_SHADER}
						fragmentShader={FRAGMENT_SHADER}
						uniforms={uniforms}
						transparent
						// Write colour, never depth, but keep depth-testing so
						// walls/model occlude the beams (no through-wall bleed).
						depthWrite={false}
						blending={THREE.AdditiveBlending}
						side={THREE.DoubleSide}
					/>
				</mesh>
			))}
			{shafts.map((shaft) =>
				shaft.dustGeometry && dustMaterial ? (
					<points
						key={`dust/${shaft.id}`}
						name={`lightShafts/dust/${shaft.id}`}
						geometry={shaft.dustGeometry}
						material={dustMaterial}
						position={shaft.position}
						quaternion={shaft.quaternion}
						scale={shaft.scale}
						renderOrder={3}
						raycast={() => null}
						dispose={null}
					/>
				) : null,
			)}
		</group>
	);
}
